import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Table from "../../../core/common/dataTable";
import { getDarContemplatedList } from "../../../core/data/redux/darSlice";
import { AppDispatch } from "../../../core/data/redux/store";
import { exportToPDF } from "../../../utils/exportToPDF";
import { exportToExcel } from "../../../utils/exportToExcel";

const columns = [
  {
    title: "Case No",
    dataIndex: "caseNo",
    key: "caseNo",
    render: (text: string) => <span className="fw-bold text-dark">{text}</span>
  },
  {
    title: "Employee No",
    dataIndex: "empNo",
    key: "empNo",
    render: (text: string) => <span className="fw-medium text-primary">{text || "-"}</span>
  },
  {
    title: "Employee Name",
    dataIndex: "employee",
    key: "employee"
  },
  {
    title: "Designation",
    dataIndex: "designation",
    key: "designation"
  },
  {
    title: "Case Type",
    dataIndex: "caseType",
    key: "caseType"
  },
  {
    title: "DA Name",
    dataIndex: "daName",
    key: "daName"
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status: string) => (
      <span className="badge bg-light text-dark border">
        {status || "UNKNOWN"}
      </span>
    )
  },
  {
    title: "Action",
    key: "actions",
    render: (text: string, record: any) => (
      <div className="d-flex gap-2">
        <Link to={`/dar-cases/details/${record.caseNo}`} className="btn btn-sm btn-icon btn-outline-info" title="View">
          <i className="ti ti-eye" style={{ fontSize: '16px' }}></i>
        </Link>
        <Link to={`/dar-cases/new?caseno=${record.caseNo}`} className="btn btn-sm btn-icon btn-outline-primary" title="Edit">
          <i className="ti ti-edit" style={{ fontSize: '16px' }}></i>
        </Link>
      </div>
    ),
  },
];

const DarContemplatedList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [empNoFilter, setEmpNoFilter] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });

  const fetchCases = async (page = 1, search = searchTerm, empFilter = empNoFilter) => {
    setLoading(true);
    try {
      const activeSearch = search.trim() || empFilter.trim();
      const response = await dispatch(getDarContemplatedList({
        page: 1,
        limit: 2000,
        filters: activeSearch ? { search_term: activeSearch } : {}
      })).unwrap();

      if (response?.status === 200 || response?.status === 201) {
        const { items } = response?.data || {};
        if (items && Array.isArray(items)) {
          const deduplicatedMap = new Map<string, any>();
          items.forEach((item: any, idx: number) => {
            if (!item) return;
            const vType = String(item.version_type || item.versionType || '').toLowerCase();
            if (vType === 'previous' || item.is_previous === true || item.isPrevious === true) return;

            const cNo = String(item.caseno || item.case_no || item.caseNo || '').trim();
            let baseCaseNo = cNo;
            let versionNum = 0;
            if (cNo && cNo !== '-') {
              const parts = cNo.split('-');
              if (parts.length >= 4 && /^\d+$/.test(parts[parts.length - 1])) {
                baseCaseNo = parts.slice(0, -1).join('-');
                versionNum = parseInt(parts[parts.length - 1], 10) || 0;
              }
            }

            const key = baseCaseNo || `${item.empno || item.emp_no || Math.random()}_${idx}`;

            if (!deduplicatedMap.has(key)) {
              deduplicatedMap.set(key, item);
            } else {
              const existing = deduplicatedMap.get(key);
              const existingCNo = String(existing.caseno || existing.case_no || existing.caseNo || '').trim();
              let existingVersionNum = 0;
              if (existingCNo && existingCNo !== '-') {
                const parts = existingCNo.split('-');
                if (parts.length >= 4 && /^\d+$/.test(parts[parts.length - 1])) {
                  existingVersionNum = parseInt(parts[parts.length - 1], 10) || 0;
                }
              }

              const existingTime = new Date(existing.updateddate || existing.updated_date || existing.createddate || existing.created_date || 0).getTime();
              const currentTime = new Date(item.updateddate || item.updated_date || item.createddate || item.created_date || 0).getTime();
              const existingId = Number(existing.id || existing._id || 0);
              const currentId = Number(item.id || item._id || 0);

              if (
                versionNum > existingVersionNum ||
                (versionNum === existingVersionNum && currentTime > existingTime) ||
                (versionNum === existingVersionNum && currentTime === existingTime && currentId >= existingId)
              ) {
                deduplicatedMap.set(key, item);
              }
            }
          });

          const currentItems = Array.from(deduplicatedMap.values());
          const contemplatedItems = currentItems.filter((item: any) => {
            const s = String(item.status || item.stage || '').trim().toUpperCase();
            return s.includes("CONTEMPLATED") || s.includes("CONTEMPLATE");
          });

          const formattedData = contemplatedItems.map((item: any, index: number) => ({
            key: item.caseno || index.toString(),
            caseNo: item.caseno,
            empNo: item.empno || item.emp_no || item.employee_no || '-',
            employee: item.empname || item.emp_name || item.employee_name || '-',
            designation: item.designation || '-',
            caseType: item.casetype || '-',
            daName: item.daname || '-',
            status: item.status || '-',
            rawItem: item
          }));
          setData(formattedData);
          setPagination(prev => ({ ...prev, page, total: formattedData.length }));
        }
      } else {
        toast.error(response?.message || "Failed to fetch cases");
      }
    } catch (err: any) {
      toast.error(err?.message || "Error fetching cases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchCases(1, searchTerm, empNoFilter);
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, empNoFilter, dispatch]);

  const handleTableChange = (paginationData: any) => {
    const newPage = paginationData.current || 1;
    const newLimit = paginationData.pageSize || 10;
    setPagination(prev => ({
      ...prev,
      page: newPage,
      limit: newLimit
    }));
    fetchCases(newPage, searchTerm, empNoFilter);
  };

  const filteredData = useMemo(() => {
    const activeFilter = empNoFilter.trim() || searchTerm.trim();
    if (!activeFilter) return data;

    const lowerFilter = activeFilter.toLowerCase();
    const cleanFilter = lowerFilter.replace(/^0+/, '');
    return data.filter(item => {
      const rawEmpNo = String(item.empNo || item.rawItem?.empno || item.rawItem?.emp_no || '').trim().toLowerCase();
      const cleanEmpNo = rawEmpNo.replace(/^0+/, '');
      const empName = String(item.employee || item.rawItem?.empname || '').trim().toLowerCase();
      const caseNo = String(item.caseNo || item.rawItem?.caseno || '').trim().toLowerCase();

      const matchEmpNo =
        (rawEmpNo.length > 0 && rawEmpNo.includes(lowerFilter)) ||
        (cleanFilter.length > 0 && cleanEmpNo.includes(cleanFilter)) ||
        (cleanEmpNo.length > 0 && lowerFilter.includes(cleanEmpNo));
      const matchName = empName.includes(lowerFilter);
      const matchCaseNo = caseNo.includes(lowerFilter);

      return matchEmpNo || matchName || matchCaseNo;
    });
  }, [data, empNoFilter, searchTerm]);

  const handleExportPDF = () => {
    const pdfColumns = [
      { title: "Case No", dataIndex: "caseNo" },
      { title: "Employee No", dataIndex: "empNo" },
      { title: "Employee Name", dataIndex: "employee" },
      { title: "Designation", dataIndex: "designation" },
      { title: "Case Type", dataIndex: "caseType" },
      { title: "DA Name", dataIndex: "daName" },
      { title: "Status", dataIndex: "status" },
    ];
    exportToPDF({
      title: "D&AR Contemplated Cases Report",
      columns: pdfColumns,
      data: filteredData,
      fileName: "DMRC_DAR_Contemplated_Cases"
    });
  };

  const handleExportExcel = () => {
    const excelColumns = [
      { title: "Case No", dataIndex: "caseNo" },
      { title: "Employee No", dataIndex: "empNo" },
      { title: "Employee Name", dataIndex: "employee" },
      { title: "Designation", dataIndex: "designation" },
      { title: "Case Type", dataIndex: "caseType" },
      { title: "DA Name", dataIndex: "daName" },
      { title: "Status", dataIndex: "status" },
    ];
    exportToExcel({
      title: "D&AR Contemplated Cases Report",
      columns: excelColumns,
      data: filteredData,
      fileName: "DMRC_DAR_Contemplated_Cases"
    });
  };

  return (
    <div className="page-wrapper">
      <div className="content">

        {/* PAGE HEADER */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">D&AR Contemplated</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to="#">Dashboard</Link></li>
                <li className="breadcrumb-item active" aria-current="page">D&AR Contemplated</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              onClick={handleExportExcel}
              className="btn btn-outline-success d-flex align-items-center shadow-sm"
              title="Export Table to Excel"
            >
              <i className="ti ti-file-spreadsheet me-2 fs-5"></i>
              Export Excel
            </button>
            <button
              type="button"
              onClick={handleExportPDF}
              className="btn btn-outline-danger d-flex align-items-center shadow-sm"
              title="Export Table to PDF"
            >
              <i className="ti ti-file-type-pdf me-2 fs-5"></i>
              Export PDF
            </button>
            <Link to="/dar-cases/new" className="btn btn-danger d-flex align-items-center shadow-sm">
              <i className="ti ti-plus me-2"></i>
              New Case
            </Link>
          </div>
        </div>

        {/* TABLE CARD */}
        <div className="card shadow-sm border-0" style={{ borderRadius: '8px' }}>
          <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ borderRadius: '8px 8px 0 0' }}>
            <h5 className="mb-0 fw-bold">Contemplated Cases History</h5>
            <div className="d-flex align-items-center gap-2">
              <div className="input-group input-group-sm" style={{ width: '220px' }}>
                <span className="input-group-text bg-light border-end-0">
                  <i className="ti ti-filter text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Filter by Emp No..."
                  value={empNoFilter}
                  onChange={(e) => setEmpNoFilter(e.target.value)}
                />
              </div>
              <span className="badge bg-light text-dark border px-3 py-2 rounded-pill shadow-none fw-medium">
                {filteredData.length} Records
              </span>
            </div>
          </div>

          <div className="card-body p-0">
            <div className="table-responsive">
              <Table
                columns={columns}
                dataSource={filteredData}
                loading={loading}
                paginationConfig={{
                  pageSize: pagination.limit,
                  current: pagination.page,
                  total: filteredData.length
                }}
                handlePagination={handleTableChange}
                handleSearch={(val: string) => setSearchTerm(val)}
                searchValue={searchTerm}
                searchPlaceholder="Search by Case No, Emp No, Name..."
              />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DarContemplatedList;

